import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import { Button } from './Button';
import { LinkTooltip } from './LinkTooltip';
import type { AddButtonLinkProps, AddButtonProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const typeClasses = {
    primary: 'btn-modern-primary',
    secondary: 'btn-modern-secondary',
    tertiary: 'btn-modern-tertiary',
    dashed: 'btn-modern-dashed',
    danger: 'btn-modern-danger',
    'danger-filled': 'btn-modern-danger-filled',
    'danger-outline': 'btn-modern-danger-outline',
    link: 'btn-modern-link',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Button.Add - "add new" button with a leading Plus icon and a `label`
 *
 * Button mode for opening drawers/modals or imperative handlers. For type-safe
 * router navigation, use the `Button.Add.Link` subcomponent instead.
 * Defaults to the `secondary` (bordered) variant.
 *
 * @example
 * // Button mode - opens a create drawer
 * <Button.Add onClick={() => openDrawer()} variant="primary" label="New incident" />
 */
export function ButtonAdd({
    label,
    variant = 'secondary',
    size = 'middle',
    disabled,
    loading,
    onClick,
    className,
}: AddButtonProps) {
    return (
        <Button
            type={variant}
            size={size}
            icon={<Plus />}
            disabled={disabled}
            loading={loading}
            onClick={onClick}
            className={className}
        >
            {label}
        </Button>
    );
}

/**
 * Base anchor component for "add"-style links
 * Styled as a modern button with a leading Plus icon
 */
const ButtonAddLinkBase = forwardRef<HTMLAnchorElement, AddButtonLinkProps>(
    (
        { label, variant = 'secondary', size = 'middle', disabled, disabledReason, className, onClick, ...props },
        ref,
    ) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'whitespace-nowrap',
                    typeClasses[variant],
                    sizeClasses[size],
                    variant === 'primary' && 'btn-color-accent',
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [variant, size, disabled, className],
        );

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Prevent both mouse and keyboard (Enter) activation when disabled —
            // pointer-events-none only blocks the mouse, leaving keyboard nav open.
            if (disabled) {
                e.preventDefault();
                return;
            }
            // Stop propagation to prevent parent click handlers (e.g., row click) from firing
            e.stopPropagation();
            onClick?.(e);
        };

        return (
            <LinkTooltip disabled={disabled} disabledReason={disabledReason}>
                <a
                    ref={ref}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : undefined}
                    className={linkClasses}
                    onClick={handleClick}
                    {...props}
                >
                    <span className="btn-icon">
                        <Plus size={iconSizes[size]} />
                    </span>
                    {label}
                </a>
            </LinkTooltip>
        );
    },
);

ButtonAddLinkBase.displayName = 'ButtonAddLinkBase';

/**
 * Button.Add.Link - Type-safe "add new" link styled as a modern button
 *
 * A preset of Button.Link with a leading Plus icon and a `label`. Uses TanStack
 * Router for type-safe `to`, `params`, and `search` props. Defaults to the
 * `secondary` (bordered) variant. Stops click propagation (so it is safe inside
 * clickable rows) and still forwards a caller-supplied `onClick`.
 *
 * @example
 * // Primary "add" action in a page header / toolbar
 * <Button.Add.Link
 *   to="/console/$orgId/compliance/incidents/add"
 *   params={{ orgId }}
 *   variant="primary"
 *   label="New incident"
 * />
 *
 * @example
 * // Tertiary inline add
 * <Button.Add.Link
 *   to="/console/$orgId/compliance/incidents/details/$id/manage/complaints"
 *   params={{ orgId, id: incidentId }}
 *   variant="tertiary"
 *   label="Add complaint"
 * />
 */
export const ButtonAddLink: LinkComponent<typeof ButtonAddLinkBase> = createLink(ButtonAddLinkBase);
